import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { InfiniteScrollObserver } from '../helpers/InfiniteScrollObserver';
import { useNoticeComponentRecords } from '../hooks/useNoticeRecords';
import React, { useMemo, useState } from 'react';
import { toDateString } from '../utils/dateUtils';
import { numberFormatter } from '../helpers/formatters';
import { SearchIcon } from '@iconicicons/react';
import { NoticeRecordHistory } from './NoticeRecordHistory';
import { MultiSelectSearch } from '../helpers/MultiSelect';
import { useSelector } from 'react-redux';
import { selectTspOptions } from '../map/slice/mapSelectors';
import { ReusableModal } from '../modals/ReusableModal';
import { getRawNoticeUrl } from '../notices/noticeSlice';
import { DropdownMenu } from '../helpers/DropdownMenu';
import { NoticeComponentRecords } from '@/types/api/NoticeComponent';
import { NoticeRecordListItem } from '@/types/api/NoticeRecord';
import { useFocusPoint } from '../hooks/useFocusPoint';

export type NoticeRecordTableProps = {
  gasDate: string;
};

export const NoticeRecordTable: React.FC<NoticeRecordTableProps> = (props) => {
  const focusPoint = useFocusPoint();
  const tspOptions = useSelector(selectTspOptions);

  const [search, setSearch] = useState('');
  const [selectedTsps, setSelectedTsps] = useState<number[]>([]);

  const [rawNoticeRecord, setRawNoticeRecord] =
    useState<NoticeRecordListItem | null>(null);
  const [priorNotices, setPriorNotices] =
    useState<NoticeComponentRecords | null>(null);
  const [componentHistoryId, setComponentHistoryId] = useState<string | null>(
    null,
  );

  const filters = useMemo(() => {
    return {
      tsps: selectedTsps,
    };
  }, [selectedTsps]);

  const {
    data: notices,
    isLoading,
    isFetching,
    exhausted,
    next,
  } = useNoticeComponentRecords(search, props.gasDate, filters);

  const rawNoticePath = useMemo(() => {
    if (!rawNoticeRecord) {
      return '';
    }
    // @ts-ignore
    return getRawNoticeUrl(rawNoticeRecord);
  }, [rawNoticeRecord]);

  return (
    <>
      <div>
        <div className="py-3 mx-3 row">
          <div className="col-6">
            <div className="input-group">
              <span className="input-group-text">
                <SearchIcon />
              </span>
              <input
                type="text"
                placeholder="Search"
                className="form-control"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="col-3">
            <MultiSelectSearch
              placeholder="Pipelines"
              options={tspOptions}
              value={selectedTsps}
              onChange={(options) =>
                setSelectedTsps(options.map((option) => option.value))
              }
            />
          </div>
        </div>
        <table className="table mt-2">
          <thead>
            <tr>
              <th>Actions</th>
              <th>Id</th>
              <th>Pipeline</th>
              <th>Identifier</th>
              <th>Direction</th>
              <th>Cycle</th>
              <th>OAC</th>
              <th>Percentage</th>
              <th>Priority</th>
              <th>Notice</th>
              <th>Start Date</th>
              <th>End Date</th>
            </tr>
          </thead>
          <tbody id="notice-component-table-body">
            {notices
              ?.filter(
                (noticeComponent) =>
                  noticeComponent.best_available_notice_record,
              )
              .map((noticeComponent) => {
                const notice = noticeComponent.best_available_notice_record;
                if (!notice) return null;
                return (
                  <tr key={notice.id}>
                    <td>
                      <DropdownMenu>
                        <li>
                          <span
                            role="button"
                            className={`dropdown-item ${
                              noticeComponent.nodes.length > 0 ? '' : 'disabled'
                            }`}
                            onClick={() =>
                              focusPoint(noticeComponent.points[0])
                            }
                          >
                            Locate
                          </span>
                        </li>
                        <li>
                          <span
                            role="button"
                            className="dropdown-item"
                            onClick={() => setPriorNotices(noticeComponent)}
                          >
                            Prior notices
                          </span>
                        </li>
                        <li>
                          <span
                            role="button"
                            className="dropdown-item"
                            onClick={() =>
                              setComponentHistoryId(
                                noticeComponent.id.toString(),
                              )
                            }
                          >
                            History
                          </span>
                        </li>
                        <li>
                          <span
                            role="button"
                            className="dropdown-item"
                            onClick={() => {
                              setRawNoticeRecord(notice);
                            }}
                          >
                            Full Notice
                          </span>
                        </li>
                      </DropdownMenu>
                    </td>
                    <td className="fw-bold">#{notice.id}</td>
                    <td>{noticeComponent.tsp_name}</td>
                    <td style={{ maxWidth: '180px' }}>{notice.identifier}</td>
                    <td>
                      {noticeComponent.flow_direction ? (
                        noticeComponent.flow_direction
                      ) : (
                        <span className="text-muted">NA</span>
                      )}
                    </td>
                    <td>{notice.nomination_cycle}</td>
                    <td>
                      {notice.capacity ? (
                        <>
                          {numberFormatter(notice.capacity)}{' '}
                          {notice.capacity_units}
                        </>
                      ) : (
                        <span className="text-muted">NA</span>
                      )}
                    </td>
                    <td className="text-center">
                      {notice.percentage ? (
                        <span>{notice.percentage}%</span>
                      ) : (
                        <span className="text-muted">NA</span>
                      )}
                    </td>
                    <td>{notice.priority}</td>
                    <td style={{ maxWidth: '256px' }}>{notice.reason}</td>
                    <td>{toDateString(notice.start_gas_date)}</td>
                    <td>{toDateString(notice.end_gas_date)}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        {!isLoading && !exhausted && (
          <InfiniteScrollObserver onScrollIn={next} />
        )}
        {(isLoading || isFetching) && (
          <div className="d-flex justify-content-center align-items-center">
            <LoadingIndicator />
          </div>
        )}
      </div>
      {Boolean(componentHistoryId) && (
        <NoticeRecordHistory
          componentId={componentHistoryId ?? ''}
          setHistoryModal={() => setComponentHistoryId(null)}
          getFlowsOnDate={() => {}}
        ></NoticeRecordHistory>
      )}
      <ReusableModal
        size="xl"
        isOpen={rawNoticeRecord !== null}
        toggle={() => setRawNoticeRecord(null)}
        header={
          rawNoticeRecord
            ? `Notice for ${rawNoticeRecord.identifier} on ${toDateString(
                rawNoticeRecord.effective_datetime ?? new Date(),
              )}`
            : ''
        }
      >
        {rawNoticePath.endsWith('.pdf') || rawNoticePath.endsWith('.html') ? (
          <iframe
            title="pdf"
            src={rawNoticePath}
            width="100%"
            height="900px"
            style={{
              maxHeight: '80vh',
              backgroundColor: 'white',
            }}
            allow="autoplay"
          />
        ) : (
          'No raw data found.'
        )}
      </ReusableModal>
      <ReusableModal
        size="xl"
        isOpen={priorNotices !== null}
        toggle={() => setPriorNotices(null)}
        header={
          <span>
            <h4>{priorNotices?.tsp_name}</h4>
            <b>#{priorNotices?.id}</b> {priorNotices?.identifier} on{' '}
            {priorNotices?.best_available_notice_record?.start_gas_date
              ? toDateString(
                  priorNotices?.best_available_notice_record?.start_gas_date,
                )
              : ''}
          </span>
        }
      >
        <div className="mt-2">
          <table className="table">
            <thead>
              <tr>
                <th>Id</th>
                <th>Identifier</th>
                <th>OAC</th>
                <th>Percentage</th>
                <th>Priority</th>
                <th>Notice</th>
                <th>Start Date</th>
                <th>End Date</th>
              </tr>
            </thead>
            <tbody>
              {priorNotices?.notice_records?.map((notice) => {
                return (
                  <tr key={notice.id}>
                    <td>{notice.id}</td>
                    <td>{notice.identifier}</td>
                    <td>
                      {notice.capacity ? (
                        `${notice.capacity} ${notice.capacity_units}`
                      ) : (
                        <span className="text-muted">NA</span>
                      )}
                    </td>
                    <td>
                      {notice.percentage ? (
                        `${notice.percentage}%`
                      ) : (
                        <span className="text-muted">NA</span>
                      )}
                    </td>
                    <td>{notice.priority}</td>
                    <td>{notice.reason}</td>
                    <td>{toDateString(notice.start_gas_date)}</td>
                    <td>{toDateString(notice.end_gas_date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ReusableModal>
    </>
  );
};
